import { z } from 'zod'
import { StoryFailure } from '../domain/story'
import type { PendingStorage, StoryClock } from '../application/ports'

const storageKey = 'cardreplay:story-pending:v1'
const pendingSchema = z
  .array(
    z
      .object({
        account: z.string().min(1),
        clientId: z.uuid(),
        reservationId: z.uuid(),
        deadline: z.number().finite(),
        action: z.enum(['cta', 'close', 'completed']).nullable(),
        impressionAccepted: z.boolean(),
      })
      .strict(),
  )
  .max(32)

export function createPendingStorage(key = storageKey): PendingStorage {
  return {
    read: () => {
      try {
        const text = sessionStorage.getItem(key)
        if (!text || text.length > 64 * 1024) return []
        const parsed = pendingSchema.safeParse(JSON.parse(text))
        return parsed.success ? parsed.data : []
      } catch {
        return []
      }
    },
    write: (entries) => {
      try {
        if (entries.length) sessionStorage.setItem(key, JSON.stringify(entries))
        else sessionStorage.removeItem(key)
      } catch {
        /* Memory delivery continues if storage is unavailable. */
      }
    },
  }
}

export function createStoryClock(): StoryClock {
  let anchor = Date.now()
  let monotonic = performance.now()
  let calibrated = false
  return {
    now: () => anchor + performance.now() - monotonic,
    observeServerTime: (time) => {
      if (time === null) return
      const current = anchor + performance.now() - monotonic
      // Late responses must not turn the calibrated clock backwards.
      anchor = calibrated ? Math.max(current, time + 1000) : time + 1000
      calibrated = true
      monotonic = performance.now()
    },
    schedule: (callback, delay) => {
      const id = window.setTimeout(callback, Math.max(0, delay))
      return () => window.clearTimeout(id)
    },
  }
}

export function storyPlatform(): 'web' | 'pwa' {
  return window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
    ? 'pwa'
    : 'web'
}

export function storyTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

export function toStoryFailure(error: unknown): StoryFailure {
  if (error instanceof StoryFailure) return error
  if (typeof error !== 'object' || error === null)
    return new StoryFailure('network', 'NETWORK')
  const status =
    'status' in error && typeof error.status === 'number'
      ? error.status
      : 'network'
  const data =
    'data' in error && typeof error.data === 'object' && error.data !== null
      ? error.data
      : null
  const code =
    data && 'code' in data && typeof data.code === 'string'
      ? data.code
      : 'REQUEST_FAILED'
  const contract = 'error' in error && error.error === 'STORY_CONTRACT_INVALID'
  const delay =
    data && 'retryAfterMs' in data && typeof data.retryAfterMs === 'number'
      ? data.retryAfterMs
      : 0
  return new StoryFailure(
    contract ? 'contract' : status,
    contract ? 'STORY_CONTRACT_INVALID' : code,
    delay,
  )
}

export function observeStoryBrowser(
  onVisibility: () => void,
  onWake: () => void,
) {
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pageshow', onWake)
  window.addEventListener('online', onWake)
  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pageshow', onWake)
    window.removeEventListener('online', onWake)
  }
}
